/**
 * SOLID Review
 * S — Strong. One concern: declare what ComponentFrontendApplication supplies — `ClientUseCases` facing
 *     the Framework and `ClientServerEvents` facing Infrastructure. No state beyond the ports, no logic.
 * O — Good. A new use case is a new forward in `ClientUseCasesPort`; the ports do not change.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. The Framework can reach only `ClientUseCases`; Infrastructure only `deliver`.
 * D — Strong. The kernel, the two contracts, the forwarding lollipop and a supplier of the context modules —
 *     never the component (see the constructor).
 */
import { ProvidedPort, PortHolder } from '@ml3d/kernel';
import { ClientUseCasesContract, type IPortClientUseCases } from '../ports/ClientUseCases.contract';
import { ClientServerEventsContract, type IPortClientServerEvents } from '../ports/ClientServerEvents.contract';
import { ClientUseCasesPort } from './ClientUseCasesPort';
import type { IEntityApplicationContexts } from './ComponentFrontendApplicationTypes';

/**
 * What ComponentFrontendApplication supplies (frontend strict-ports plan, FE1, 2026-10-03) — the twin of
 * the backend's `ComponentApplicationProvidedPorts`: one lollipop facing the Framework, one facing
 * Infrastructure.
 *
 * ⚠ HANDED A SUPPLIER OF THE CONTEXT MODULES, NEVER THE COMPONENT (frontend-application-oo P7, the backend's A5): naming
 * `ComponentFrontendApplication` here, even `import type`, closes a module cycle `no-circular` rejects, and the modules exist
 * only once `build()` has run — so both lollipops read them at CALL time.
 *
 * ⚠ ASSIGNED IN THE CONSTRUCTOR BODY, NOT AS FIELD INITIALISERS. The frontend's `tsconfig.json` sets
 * `useDefineForClassFields`, so an initialiser would run before the supplier exists. The fields are
 * DECLARED, which fixes their order for `describePorts`, and assigned where the supplier is in scope.
 *
 * ⚠ WALKED BECAUSE THIS CLASS EXTENDS `PortHolder`; a port must be an own field of exactly one holder.
 */
export class ComponentFrontendApplicationProvidedPorts extends PortHolder {
  /** Everything React may ask of the Application. */
  readonly clientUseCasesPort: ProvidedPort<IPortClientUseCases>;
  /** Where Infrastructure delivers the server's events, one call per event. */
  readonly clientServerEventsPort: ProvidedPort<IPortClientServerEvents>;

  constructor(contexts: () => IEntityApplicationContexts) {
    super();
    this.clientUseCasesPort = new ProvidedPort<IPortClientUseCases>('ClientUseCases', new ClientUseCasesPort(contexts), ClientUseCasesContract);
    // Every delivery to the handler of its kind (`ServerEvents`, P3), read at call time.
    const events: IPortClientServerEvents = { deliver: (ctx, event) => contexts().events.deliver(ctx, event) };
    this.clientServerEventsPort = new ProvidedPort<IPortClientServerEvents>('ClientServerEvents', events, ClientServerEventsContract);
  }
}
